import React, { useState, Fragment, useContext, useEffect } from 'react';
import { Cell } from 'react-foundation';
import { Form, Formik } from 'formik';
import classnames from 'classnames';
import * as Yup from 'yup';

import styles from './InviteClinicianPage.module.scss';

import PrimaryButton from '../../components/common/buttons/PrimaryButton';
import Loading from '../../components/common/Loading';
import FieldInput from '../../components/common/fields/FieldInput/FieldInput';
import DoctorLeftNav from '../../components/nav/DoctorLeftNav';
import MessageModal from '../../components/MessageModal';

import AuthContext from '../../context/AuthContext';

import messages from './messages';
import useIntl from '../../hooks/useIntl';

import { addToDataLayer } from '../../utils/common';
import { INVITED_CLINICIAN_INVITED_STATUS } from '../../constants';

import { inviteClinician } from '../../api/doctor';

const tabs = ['EMAIL', 'SMS'];

const InviteClinicianPage = () => {
  const { formatMessage } = useIntl();

  const { authUser } = useContext(AuthContext);

  const [error, setError] = useState('');
  const [dataLoading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(tabs[0]);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [displayMessage, setDisplayMessage] = useState({
    title: '',
    message: '',
  });

  const EmailSchema = Yup.object().shape({
    firstName: Yup.string().required('Field Required'),
    email: Yup.string().email().required('Field Required'),
  });
  const SmsSchema = Yup.object().shape({
    firstName: Yup.string().required('Field Required'),
    phone: Yup.string()
      .min(10, 'Please enter valid phone number')
      .max(10, 'Please enter valid phone number')
      .matches(
        /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/,
        'Please enter valid phone number',
      )
      .required('Phone number is required.'),
  });

  useEffect(() => {
    if (authUser.id) {
      const eventData = {
        event: 'ga4_event',
        properties: {
          event_name: 'invite_clinician',
          step_name: 'Invite_clinician_load',
          clinician_id: authUser.id || '',
          clinician_name: authUser.first_name || '',
          user_type: authUser.profile_type,
        },
      };
      addToDataLayer(eventData);
    }
  }, [authUser]);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const { email, firstName, phone } = values;

      const eventData = {
        event: 'ga4_event',
        properties: {
          event_name: 'invite_clinician',
          step_name: 'Invite_clinician_submit',
          clinician_id: authUser.id || '',
          clinician_name: authUser.first_name || '',
          user_type: authUser.profile_type,
        },
      };
      addToDataLayer(eventData);

      await inviteClinician({
        email: email === '' ? null : email,
        phone: phone === '' ? null : phone,
        first_name: firstName,
        doctor_id: authUser.id,
        clinic_id: authUser.clinic_id,
      });

      setLoading(false);
      setDisplayMessage({
        type: 'success',
        title: formatMessage(messages.modalSuccessText),
        text: '',
      });

      setShowMessageModal(true);
    } catch (error) {
      let errorMsg = '';
      if (error?.response?.data?.message) {
        errorMsg = error.response.data.message;
      }
      setError(errorMsg);
      setLoading(false);
      setDisplayMessage({
        type: 'error',
        title: '',
        text: errorMsg || formatMessage(messages.modalFailureText),
      });
      setShowMessageModal(true);
      throw error;
    }
  };

  const onclickTab = (tab) => {
    setActiveTab(tab);
  };

  return (
    <Fragment>
      <div className={styles.container}>
        <div medium={3} className={classnames(styles.leftnav, 'desktop-only')}>
          <DoctorLeftNav />
        </div>
        {dataLoading ? (
          <Loading />
        ) : (
          <Fragment>
            <div className={styles.lineArt}>
              <img src={require(`../../img/LineArt.svg`)} alt="line-art" />
            </div>
            <div className={styles.contentDiv}>
              <div
                medium={3}
                className={classnames(styles.toptnav, 'mobile-only')}
              />

              <Formik
                initialValues={{ email: '', firstName: '', phone: '' }}
                validationSchema={
                  activeTab === 'EMAIL' ? EmailSchema : SmsSchema
                }
                onSubmit={async (values) => {
                  handleSubmit(values);
                }}
              >
                {({
                  errors,
                  handleChange,
                  values,
                  touched,
                  handleBlur,
                  setFieldValue,
                  isValid,
                }) => {
                  return (
                    <Form className={styles['content']}>
                      <div className={classnames(styles[`form-div`])}>
                        <h2 data-testid="invite clinician header">
                          Invite Clinician
                        </h2>
                        <div
                          className={styles['sub-text']}
                          data-testid="invite-patient subtext"
                        >
                          Invite Clinician To Join{' '}
                          {authUser?.clinicDetails?.clinic_name ||
                            'Eve Health Systems'}{' '}
                          Platform
                        </div>
                        <nav className={styles.tabs}>
                          <div>
                            <p data-testid="clinician send_invitation">
                              How do you want to send your invitation?
                            </p>
                          </div>
                          <div>
                            {tabs.map((tab) => (
                              <button
                                key={tab}
                                className={[
                                  styles.tab,
                                  activeTab === tab ? styles.activeTab : '',
                                ].join(' ')}
                                type="button"
                                onClick={() => {
                                  setFieldValue('email', '');
                                  setFieldValue('phone', '');
                                  onclickTab(tab);
                                }}
                              >
                                <span data-testid={tab}>{tab}</span>
                              </button>
                            ))}
                          </div>
                        </nav>
                        <div className={styles.formInput}>
                          <Cell small={12} medium={12}>
                            <FieldInput
                              label="First Name"
                              className={styles.input}
                              name="firstName"
                              errorText={errors.firstName}
                              onChange={(e) => {
                                setFieldValue('firstName', e.target.value);
                              }}
                              onBlur={handleBlur}
                              value={values.firstName}
                              placeholder="First Name"
                              data-testid="clinician name"
                              label-testid="clinician name label"
                              error-testid="clinician name error"
                            />
                          </Cell>
                          <Cell small={12} medium={12}>
                            {activeTab === 'EMAIL' ? (
                              <FieldInput
                                label="email"
                                className={styles.input}
                                name="email"
                                errorText={errors.email}
                                onChange={(e) => {
                                  setError('');
                                  setFieldValue('email', e.target.value);
                                }}
                                onBlur={handleBlur}
                                value={values.email}
                                placeholder="Clinician's Email Address"
                                data-testid="clinician email"
                                label-testid="clinician email label"
                                error-testid="clinician email error"
                              />
                            ) : (
                              <FieldInput
                                label="Mobile number"
                                className={styles.input}
                                name="phone"
                                errorText={errors.phone}
                                onChange={(e) => {
                                  setError('');
                                  setFieldValue('phone', e.target.value);
                                }}
                                onBlur={handleBlur}
                                value={values.phone}
                                placeholder="Mobile number"
                                data-testid="clinician phone"
                                label-testid="clinician phone label"
                                error-testid="clinician phone error"
                              />
                            )}
                          </Cell>
                        </div>
                        {error && (
                          <div
                            className={styles.error}
                            data-testid="invite_clinician error"
                          >
                            {error}
                          </div>
                        )}
                        <div className={classnames(styles[`button-row`])}>
                          <PrimaryButton
                            label="Invite"
                            className={classnames(styles[`button`])}
                            disabled={!isValid}
                            data-testid="invite button"
                          />
                        </div>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          </Fragment>
        )}
      </div>

      {showMessageModal && (
        <MessageModal
          message={displayMessage}
          onCloseModal={() => setShowMessageModal(false)}
          redirectUrl={'/doctor/my-account'}
        />
      )}
    </Fragment>
  );
};

export default InviteClinicianPage;
